import { useState, useEffect, useCallback } from "react";
import { useAdminApi, toLocalDateStr, fmtDate, fmtMoney, exportXLSX } from "../../adminUtils";
import ExportBar from "./ExportBar";

// R11 — Video Rentals: who rents, how often, which videos, which codes.
// Select students and copy their emails to paste into a rental code's allowed emails.
export default function VideoRentalsReport() {
  const { API, AUTH } = useAdminApi();
  const today = toLocalDateStr(new Date());
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 89); return toLocalDateStr(d); });
  const [to, setTo]     = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [copyState, setCopyState] = useState("idle");

  const load = useCallback(async () => {
    setLoading(true); setLoadError(false);
    try {
      const res = await fetch(`${API}/reports/video-rentals?from=${from}&to=${to}`, { headers: AUTH });
      if (!res.ok) throw new Error();
      setData(await res.json());
      setSelected(new Set());
    } catch { setData(null); setLoadError(true); }
    finally { setLoading(false); }
  }, [from, to, API, AUTH]);

  useEffect(() => { load(); }, [load]);

  const students = data?.students ?? [];
  const allSelected = students.length > 0 && selected.size === students.length;
  const toggle = email => setSelected(prev => {
    const next = new Set(prev);
    next.has(email) ? next.delete(email) : next.add(email);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(students.map(s => s.email)));

  const copyEmails = async () => {
    const text = [...selected].join(", ");
    try { await navigator.clipboard.writeText(text); setCopyState("copied"); }
    catch { window.prompt("Copy these emails:", text); }
    setTimeout(() => setCopyState("idle"), 2500);
  };

  const doExport = () => {
    exportXLSX("Video Rentals", students.map(s => ({
      Name: s.name, Email: s.email, Plan: s.plan || "—",
      Rentals: s.rentals, Paid: s.paid, Free: s.free,
      Spent: +(s.spentCents / 100).toFixed(2),
      "Last rented": fmtDate(s.lastRentedAt),
      Videos: s.titles.join("; "),
    })), `florium-video-rentals-${from}-${to}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label" htmlFor="vr-from">From</label>
          <input id="vr-from" type="date" className="adm-input" value={from} max={to} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label" htmlFor="vr-to">To</label>
          <input id="vr-to" type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {loadError && <p className="adm-error">Couldn't load video rentals — check your connection and refresh.</p>}

      {data && !loading && (
        <>
          <div className="adm-report-summary-row">
            {[
              { label: "Rentals",       value: data.summary.rentals },
              { label: "Students",      value: data.summary.students },
              { label: "Paid",          value: data.summary.paid },
              { label: "Free",          value: data.summary.free },
              { label: "Used a code",   value: data.summary.withCode },
              { label: "Revenue",       value: fmtMoney(data.summary.revenueCents / 100) },
            ].map(s => (
              <div key={s.label} className="adm-summary-chip">
                <span className="adm-summary-val">{s.value}</span>
                <span className="adm-summary-lbl">{s.label}</span>
              </div>
            ))}
          </div>
          <ExportBar onXLSX={doExport} disabled={students.length === 0} />

          {students.length === 0 ? (
            <p className="adm-empty">No video rentals in this period.</p>
          ) : (
            <>
              <h3 className="adm-report-subhead">Students</h3>
              <div className="adm-remind-confirm">
                <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={selected.size === 0} onClick={copyEmails}>
                  {copyState === "copied" ? "✓ Copied" : `Copy ${selected.size} email${selected.size !== 1 ? "s" : ""}`}
                </button>
                <span className="adm-hint">Paste into a rental code's allowed emails (Coupons) to give a free or discounted viewing.</span>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" aria-label="Select all" checked={allSelected} onChange={toggleAll} /></th>
                      <th>Student</th><th>Plan</th><th>Rentals</th><th>Paid</th><th>Free</th><th>Spent</th><th>Last rented</th><th>Videos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => (
                      <tr key={s.email}>
                        <td><input type="checkbox" aria-label={`Select ${s.email}`} checked={selected.has(s.email)} onChange={() => toggle(s.email)} /></td>
                        <td>{s.name ? <><strong>{s.name}</strong><br /></> : null}<span className="adm-muted">{s.email}</span></td>
                        <td>{s.plan || "—"}</td>
                        <td><strong>{s.rentals}</strong></td>
                        <td>{s.paid}</td>
                        <td>{s.free}</td>
                        <td>{fmtMoney(s.spentCents / 100)}</td>
                        <td>{fmtDate(s.lastRentedAt)}</td>
                        <td>{s.titles.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="adm-report-subhead">Videos</h3>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead><tr><th>Video</th><th>Rentals</th><th>Students</th><th>Free</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {data.videos.map(v => (
                      <tr key={v.title}>
                        <td>{v.title}</td><td><strong>{v.rentals}</strong></td><td>{v.students}</td><td>{v.free}</td><td>{fmtMoney(v.revenueCents / 100)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.codes.length > 0 && (
                <>
                  <h3 className="adm-report-subhead">Codes used</h3>
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead><tr><th>Code</th><th>Rentals</th><th>Free</th><th>Revenue</th></tr></thead>
                      <tbody>
                        {data.codes.map(c => (
                          <tr key={c.code}>
                            <td><strong>{c.code}</strong></td><td>{c.rentals}</td><td>{c.free}</td><td>{fmtMoney(c.revenueCents / 100)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
